// PRO Drift Auditor -- side-by-side 100% stacked composition over time:
// "Unchecked Drift" vs "AI Regime PRO" weights, so the user sees how winners
// bloat under drift but are actively tilted under the AI engine.

import React, { useMemo, useState } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Layers } from 'lucide-react';

import { Card } from './Card';
import { tickerColor } from '../format';
import type { ProTimeSeriesPoint } from '../types';

type Mode = 'drifted' | 'ai_pro';

interface Props {
    data: ProTimeSeriesPoint[];
    tickers: string[];
}

const StackedComposition: React.FC<{
    data: ProTimeSeriesPoint[];
    tickers: string[];
    weightKey: 'drifted_weights' | 'ai_pro_weights';
}> = ({ data, tickers, weightKey }) => {
    const rows = useMemo(
        () =>
            data.map((p) => {
                const flat: Record<string, number | string> = { date: p.date };
                const w = p[weightKey];
                for (const t of tickers) flat[t] = w[t] ?? 0;
                return flat;
            }),
        [data, tickers, weightKey],
    );
    return (
        <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={rows} margin={{ top: 6, right: 8, left: 2, bottom: 0 }} stackOffset="expand">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.3} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} minTickGap={56} tickLine={false} />
                    <YAxis
                        tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                        width={38}
                        tickLine={false}
                        axisLine={false}
                    />
                    <Tooltip
                        formatter={(v: number, name) => [`${v.toFixed(2)}%`, name]}
                        contentStyle={{
                            borderRadius: 12,
                            border: '1px solid #e2e8f0',
                            fontSize: 12,
                            background: 'rgba(255,255,255,0.97)',
                        }}
                    />
                    {tickers.map((t, i) => (
                        <Area
                            key={t}
                            type="monotone"
                            dataKey={t}
                            stackId="1"
                            stroke={tickerColor(i)}
                            fill={tickerColor(i)}
                            fillOpacity={0.82}
                            strokeWidth={0.5}
                        />
                    ))}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export const WeightDriftChart: React.FC<Props> = ({ data, tickers }) => {
    const [mode, setMode] = useState<Mode>('drifted');
    return (
        <Card
            title="Allocation Composition Over Time"
            subtitle="How your weights evolve — compare passive drift with the Forge Ultra regime-tilted book."
            icon={<Layers className="h-4 w-4" />}
            right={
                <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800/60">
                    {(['drifted', 'ai_pro'] as Mode[]).map((m) => (
                        <button
                            key={m}
                            type="button"
                            onClick={() => setMode(m)}
                            className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${
                                mode === m
                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400'
                            }`}
                        >
                            {m === 'drifted' ? 'Unchecked Drift' : 'Forge Ultra Regime PRO'}
                        </button>
                    ))}
                </div>
            }
        >
            <StackedComposition
                data={data}
                tickers={tickers}
                weightKey={mode === 'drifted' ? 'drifted_weights' : 'ai_pro_weights'}
            />
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {tickers.map((t, i) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: tickerColor(i) }} />
                        {t}
                    </span>
                ))}
            </div>
        </Card>
    );
};
